<script lang="ts">
  // Demo pública del Hospital — vitrina navegable de solo lectura con datos
  // sintéticos. NO requiere login (App.svelte la monta en /demo y en el host
  // *.workers.dev sin inicializar Keycloak).
  //
  // El menú de módulos va ARRIBA y es clickeable: cada módulo muestra un panel
  // de ejemplo. Arranca en "Resumen" (no en Camas).

  const REGISTRO = 'https://app.toscana-hospital.com/registro?plan=hospital-pro';
  const EDICIONES = 'https://www.toscanasystems.com/pages/sistema-medico/clinica-hospital.php#hospital-privado';

  type Modulo =
    | 'resumen' | 'camas' | 'expediente' | 'enfermeria' | 'quirofano'
    | 'laboratorio' | 'imagenologia' | 'farmacia' | 'facturacion'
    | 'tesoreria' | 'contabilidad' | 'nomina' | 'reportes';

  // Orden por diferenciadores primero (FEL, Farmacia+POS, LIS/RIS, Quirófano),
  // luego el flujo clínico y por último lo administrativo.
  const menu: { id: Modulo; label: string }[] = [
    { id: 'resumen',     label: '📊 Resumen' },
    { id: 'facturacion', label: '🧾 Facturación FEL' },
    { id: 'farmacia',    label: '💊 Farmacia + POS' },
    { id: 'laboratorio', label: '🧪 Laboratorio (LIS)' },
    { id: 'imagenologia',label: '🩻 Imagenología (RIS)' },
    { id: 'quirofano',   label: '🔪 Quirófano' },
    { id: 'camas',       label: '🛏️ Mapa de camas' },
    { id: 'expediente',  label: '📋 Expediente' },
    { id: 'enfermeria',  label: '💉 Enfermería / Kárdex' },
    { id: 'tesoreria',   label: '🏦 Tesorería' },
    { id: 'contabilidad',label: '📊 Contabilidad' },
    { id: 'nomina',      label: '👥 Nómina' },
    { id: 'reportes',    label: '📈 Reportes MSPAS' },
  ];

  let vista = $state<Modulo>('resumen');

  const kpis = [
    { label: 'Camas ocupadas', value: '34 / 48', hint: '71 % de ocupación' },
    { label: 'Ingresos hoy', value: '12', hint: '4 por urgencias' },
    { label: 'Cirugías programadas', value: '6', hint: '2 en quirófano ahora' },
    { label: 'Facturado mes', value: 'Q 842,500', hint: 'FEL SAT + seguros' },
  ];

  type EstadoCama = 'ocupada' | 'libre' | 'limpieza' | 'mantenimiento';
  const leyenda: { e: EstadoCama; label: string }[] = [
    { e: 'ocupada', label: 'Ocupada' },
    { e: 'libre', label: 'Libre' },
    { e: 'limpieza', label: 'En limpieza' },
    { e: 'mantenimiento', label: 'Mantenimiento' },
  ];
  const camas: { n: string; e: EstadoCama }[] = (() => {
    const patron: EstadoCama[] = ['ocupada','ocupada','libre','ocupada','limpieza','ocupada','ocupada','mantenimiento','libre','ocupada'];
    const out: { n: string; e: EstadoCama }[] = [];
    for (let i = 1; i <= 48; i++) out.push({ n: String(100 + i), e: patron[(i * 3 + i % 5) % patron.length] });
    return out;
  })();

  const ingresos = [
    { hora: '07:40', paciente: 'María López H.',     servicio: 'Medicina Interna', cama: '204', estado: 'Estable' },
    { hora: '08:15', paciente: 'Carlos Méndez P.',   servicio: 'Cardiología',      cama: '112', estado: 'Observación' },
    { hora: '09:05', paciente: 'Ana Ruiz C.',        servicio: 'Cirugía General',  cama: '308', estado: 'Pre-quirúrgico' },
    { hora: '10:20', paciente: 'Pedro Gómez A.',     servicio: 'Urgencias',        cama: 'URG-3', estado: 'Crítico' },
    { hora: '11:30', paciente: 'Lucía Hernández S.', servicio: 'Ginecología',      cama: '221', estado: 'Estable' },
  ];

  const signos = [
    { label: 'TA',   value: '128/82', tone: 'warn' },
    { label: 'FC',   value: '76 lpm', tone: 'ok' },
    { label: 'FR',   value: '16',     tone: 'ok' },
    { label: 'SpO₂', value: '97 %',   tone: 'ok' },
    { label: 'T°',   value: '36.7',   tone: 'ok' },
    { label: 'Glu',  value: '142',    tone: 'warn' },
  ];

  const evolucion = [
    { hora: '06:30', autor: 'Enf. J. Ramírez', txt: 'Paciente afebril, tolera vía oral. Diuresis adecuada.' },
    { hora: '08:10', autor: 'Dr. R. Castellanos', txt: 'Continúa antihipertensivo. Pendiente control de electrolitos.' },
    { hora: '11:45', autor: 'Enf. M. Soto', txt: 'Administrado Enalapril 10mg VO. TA 128/82.' },
  ];

  const kardex = [
    { hora: '06:00', med: 'Enalapril 10 mg', via: 'VO', estado: 'Administrado' },
    { hora: '08:00', med: 'Omeprazol 40 mg', via: 'IV', estado: 'Administrado' },
    { hora: '12:00', med: 'Metformina 850 mg', via: 'VO', estado: 'Pendiente' },
    { hora: '14:00', med: 'Ceftriaxona 1 g', via: 'IV', estado: 'Pendiente' },
    { hora: '20:00', med: 'Enalapril 10 mg', via: 'VO', estado: 'Programado' },
  ];

  const quirofano = [
    { hora: '08:00', sala: 'Quirófano 1', proc: 'Colecistectomía laparoscópica', cirujano: 'Dr. R. Castellanos', estado: 'En curso' },
    { hora: '10:30', sala: 'Quirófano 2', proc: 'Cesárea programada',            cirujano: 'Dra. L. Fuentes',    estado: 'Programada' },
    { hora: '13:00', sala: 'Quirófano 1', proc: 'Hernioplastía inguinal',        cirujano: 'Dr. A. Morales',     estado: 'Programada' },
  ];

  const laboratorio = [
    { orden: 'LAB-10482', paciente: 'María López H.',   examen: 'Química sanguínea', estado: 'Resultado listo', valor: 'Glu 142 ↑' },
    { orden: 'LAB-10483', paciente: 'Carlos Méndez P.', examen: 'Troponina I',       estado: 'En proceso',      valor: '—' },
    { orden: 'LAB-10484', paciente: 'Ana Ruiz C.',      examen: 'Hematología',       estado: 'Tomado',          valor: '—' },
    { orden: 'LAB-10485', paciente: 'Pedro Gómez A.',   examen: 'Gases arteriales',  estado: 'Resultado listo', valor: 'pH 7.31 ↓' },
  ];

  const imagenologia = [
    { orden: 'IMG-5521', paciente: 'Pedro Gómez A.',   estudio: 'TAC de cráneo',     estado: 'Informado',  modalidad: 'CT' },
    { orden: 'IMG-5522', paciente: 'Carlos Méndez P.', estudio: 'Radiografía tórax', estado: 'Adquirido',  modalidad: 'CR' },
    { orden: 'IMG-5523', paciente: 'Lucía Hernández S.', estudio: 'Ultrasonido obst.', estado: 'Programado', modalidad: 'US' },
  ];

  const farmacia = [
    { cod: '7501-0012', art: 'Paracetamol 500mg (caja)', stock: 240, min: 50, precio: 'Q 18.00' },
    { cod: '7501-0048', art: 'Amoxicilina 500mg (caja)', stock: 38,  min: 40, precio: 'Q 65.00' },
    { cod: '7501-0091', art: 'Suero fisiológico 1L',     stock: 120, min: 30, precio: 'Q 22.50' },
    { cod: '7501-0155', art: 'Jeringa 5ml (unidad)',     stock: 1500,min: 300,precio: 'Q 1.20' },
  ];
  const ticket = [
    { item: 'Paracetamol 500mg x10', cant: 2, total: 'Q 36.00' },
    { item: 'Consulta externa',       cant: 1, total: 'Q 150.00' },
    { item: 'Suero fisiológico 1L',   cant: 1, total: 'Q 22.50' },
  ];

  const facturas = [
    { doc: 'FACT-A-004821', cliente: 'María López H.',   total: 'Q 1,250.00', fel: 'Certificado' },
    { doc: 'FACT-A-004822', cliente: 'Seguros El Roble', total: 'Q 8,940.00', fel: 'Certificado' },
    { doc: 'FACT-A-004823', cliente: 'Carlos Méndez P.', total: 'Q 420.00',   fel: 'Pendiente' },
  ];

  const tesoreria = [
    { fecha: '07 Jun', concepto: 'Depósito BAC — caja día',  tipo: 'Ingreso', monto: '+ Q 42,300' },
    { fecha: '07 Jun', concepto: 'Pago proveedor MediSupplyGT', tipo: 'Egreso', monto: '− Q 12,800' },
    { fecha: '06 Jun', concepto: 'Transferencia Seguros El Roble', tipo: 'Ingreso', monto: '+ Q 8,940' },
    { fecha: '06 Jun', concepto: 'Planilla quincena', tipo: 'Egreso', monto: '− Q 96,500' },
  ];

  const contabilidad = [
    { cuenta: '4101 · Ingresos por servicios', debe: '—', haber: 'Q 842,500' },
    { cuenta: '5101 · Costo de insumos',        debe: 'Q 318,200', haber: '—' },
    { cuenta: '5201 · Sueldos y salarios',      debe: 'Q 193,000', haber: '—' },
    { cuenta: '1102 · Bancos',                  debe: 'Q 331,300', haber: '—' },
  ];

  const nomina = [
    { empleado: 'Dr. R. Castellanos', puesto: 'Cirujano',     liquido: 'Q 28,400' },
    { empleado: 'Enf. M. Soto',       puesto: 'Enfermería',   liquido: 'Q 7,200' },
    { empleado: 'Lic. P. Aguilar',    puesto: 'Administración',liquido: 'Q 9,800' },
    { empleado: 'T. A. Ramírez',      puesto: 'Lab. clínico', liquido: 'Q 6,500' },
  ];

  const reportes = [
    { ind: 'Egresos hospitalarios (mes)', valor: '218' },
    { ind: 'Promedio de estancia',        valor: '3.4 días' },
    { ind: 'Tasa de ocupación',           valor: '71 %' },
    { ind: 'Partos atendidos',            valor: '41' },
    { ind: 'Notif. enfermedades (SIGSA)', valor: '7' },
  ];
</script>

<div class="demo">
  <div class="banner">
    <span>✨ <strong>Vista demo</strong> — datos ficticios. Navegá los módulos en el menú de arriba.</span>
    <a href={REGISTRO}>Activar mi hospital · 30 días gratis →</a>
  </div>

  <header class="top">
    <div class="brand">
      <img src="https://www.toscanasystems.com/assets/img/logo.png" alt="Toscana Systems" />
      <div class="title">
        <div class="edicion">Demo · Hospital privado</div>
        <small>Plataforma hospitalaria integral · multitenant en la nube</small>
      </div>
    </div>
    <a class="btn ghost" href={REGISTRO}>Empezar 30 días gratis →</a>
  </header>

  <!-- MENÚ DE MÓDULOS (arriba, clickeable) -->
  <nav class="modnav" aria-label="Módulos">
    {#each menu as m (m.id)}
      <button class:active={vista === m.id} onclick={() => (vista = m.id)}>{m.label}</button>
    {/each}
  </nav>

  <main>
    {#if vista === 'resumen'}
      <section class="hero">
        <div class="hero-emoji">🏥</div>
        <div class="hero-text">
          <h1>Un día típico en el hospital</h1>
          <p>Camas, expediente, quirófano, laboratorio, imagenología, farmacia, facturación FEL y administración — todo en una sola plataforma. Elegí un módulo arriba para verlo en acción.</p>
          <div class="hero-cta">
            <a class="btn primary" href={REGISTRO}>Empezar 30 días gratis →</a>
            <a class="btn ghost-light" href={EDICIONES} target="_blank" rel="noopener">Conocer todas las ediciones</a>
          </div>
        </div>
      </section>

      <section class="kpis">
        {#each kpis as k (k.label)}
          <div class="kpi">
            <div class="kpi-label">{k.label}</div>
            <div class="kpi-value">{k.value}</div>
            <div class="kpi-hint">{k.hint}</div>
          </div>
        {/each}
      </section>

      <section class="card">
        <div class="card-head"><span>📥 Ingresos de hoy</span><span class="muted">{ingresos.length} pacientes</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Hora</th><th>Paciente</th><th>Servicio</th><th>Cama</th><th>Estado</th></tr></thead>
            <tbody>
              {#each ingresos as i (i.hora)}
                <tr><td class="mono">{i.hora}</td><td>{i.paciente}</td><td class="muted">{i.servicio}</td><td class="mono">{i.cama}</td><td><span class="badge">{i.estado}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'camas'}
      <section class="card">
        <div class="card-head"><span>🛏️ Semáforo de camas</span><span class="muted">48 camas · 3 pisos · 71 % ocupación</span></div>
        <div class="card-body">
          <div class="camas">
            {#each camas as c (c.n)}<div class="cama {c.e}" title="Cama {c.n} · {c.e}">{c.n}</div>{/each}
          </div>
          <div class="leyenda">
            {#each leyenda as l (l.e)}<span><i class="dot {l.e}"></i>{l.label}</span>{/each}
          </div>
        </div>
      </section>

    {:else if vista === 'expediente'}
      <section class="grid2">
        <div class="card">
          <div class="card-head"><span>📋 Expediente · María López H.</span><span class="muted">Exp. 0042-2026</span></div>
          <div class="card-body">
            <div class="datos">
              <div><b>Edad</b> 58 años</div><div><b>Sexo</b> F</div>
              <div><b>Servicio</b> Medicina Interna</div><div><b>Cama</b> 204</div>
              <div><b>Alergias</b> Penicilina</div><div><b>Tipo sangre</b> O+</div>
            </div>
            <p class="dx"><strong>Dx:</strong> I10 — Hipertensión esencial</p>
            <div class="card-sub">Notas de evolución</div>
            <ul class="evo">
              {#each evolucion as e (e.hora)}
                <li><span class="mono">{e.hora}</span><span><strong>{e.autor}</strong><small>{e.txt}</small></span></li>
              {/each}
            </ul>
          </div>
        </div>
        <div class="card">
          <div class="card-head"><span>🩺 Signos vitales</span></div>
          <div class="card-body">
            <div class="signos">
              {#each signos as s (s.label)}<div class="signo {s.tone}"><div class="s-label">{s.label}</div><div class="s-value">{s.value}</div></div>{/each}
            </div>
          </div>
        </div>
      </section>

    {:else if vista === 'enfermeria'}
      <section class="card">
        <div class="card-head"><span>💉 Kárdex de enfermería · cama 204</span><span class="muted">Hoy</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Hora</th><th>Medicamento</th><th>Vía</th><th>Estado</th></tr></thead>
            <tbody>
              {#each kardex as k (k.hora + k.med)}
                <tr><td class="mono">{k.hora}</td><td>{k.med}</td><td class="mono">{k.via}</td>
                <td><span class="badge {k.estado === 'Administrado' ? 'ok' : k.estado === 'Pendiente' ? 'warn' : ''}">{k.estado}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'quirofano'}
      <section class="card">
        <div class="card-head"><span>🔪 Programación de quirófano · hoy</span><span class="muted">2 salas</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Hora</th><th>Sala</th><th>Procedimiento</th><th>Cirujano</th><th>Estado</th></tr></thead>
            <tbody>
              {#each quirofano as q (q.hora + q.sala)}
                <tr><td class="mono">{q.hora}</td><td>{q.sala}</td><td>{q.proc}</td><td class="muted">{q.cirujano}</td>
                <td><span class="badge {q.estado === 'En curso' ? 'warn' : ''}">{q.estado}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'laboratorio'}
      <section class="card">
        <div class="card-head"><span>🧪 Órdenes de laboratorio (LIS)</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Orden</th><th>Paciente</th><th>Examen</th><th>Resultado</th><th>Estado</th></tr></thead>
            <tbody>
              {#each laboratorio as l (l.orden)}
                <tr><td class="mono">{l.orden}</td><td>{l.paciente}</td><td class="muted">{l.examen}</td><td class="mono">{l.valor}</td>
                <td><span class="badge {l.estado === 'Resultado listo' ? 'ok' : ''}">{l.estado}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'imagenologia'}
      <section class="card">
        <div class="card-head"><span>🩻 Órdenes de imagenología (RIS/PACS)</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Orden</th><th>Paciente</th><th>Estudio</th><th>Modalidad</th><th>Estado</th></tr></thead>
            <tbody>
              {#each imagenologia as im (im.orden)}
                <tr><td class="mono">{im.orden}</td><td>{im.paciente}</td><td class="muted">{im.estudio}</td><td class="mono">{im.modalidad}</td>
                <td><span class="badge {im.estado === 'Informado' ? 'ok' : ''}">{im.estado}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'farmacia'}
      <section class="grid2">
        <div class="card">
          <div class="card-head"><span>🧾 Punto de venta (POS)</span><span class="muted">Caja 1 · turno mañana</span></div>
          <div class="card-body">
            <ul class="ticket">
              {#each ticket as t (t.item)}<li><span>{t.cant}× {t.item}</span><span class="mono">{t.total}</span></li>{/each}
            </ul>
            <div class="ticket-total"><span>Total</span><span class="mono">Q 208.50</span></div>
            <div class="pos-actions">
              <span class="btn primary sm">Cobrar y facturar (FEL) →</span>
              <span class="muted">Genera factura electrónica SAT al cobrar.</span>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-head"><span>💊 Inventario</span></div>
          <div class="card-body nopad">
            <table>
              <thead><tr><th>Código</th><th>Artículo</th><th>Stock</th><th>Precio</th></tr></thead>
              <tbody>
                {#each farmacia as f (f.cod)}
                  <tr><td class="mono">{f.cod}</td><td>{f.art}</td>
                  <td><span class="badge {f.stock <= f.min ? 'warn' : 'ok'}">{f.stock}</span></td><td class="mono">{f.precio}</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </section>

    {:else if vista === 'facturacion'}
      <section class="card">
        <div class="card-head"><span>🧾 Facturación electrónica (FEL SAT)</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Documento</th><th>Cliente</th><th>Total</th><th>Estado FEL</th></tr></thead>
            <tbody>
              {#each facturas as fa (fa.doc)}
                <tr><td class="mono">{fa.doc}</td><td>{fa.cliente}</td><td class="mono">{fa.total}</td>
                <td><span class="badge {fa.fel === 'Certificado' ? 'ok' : 'warn'}">{fa.fel}</span></td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'tesoreria'}
      <section class="card">
        <div class="card-head"><span>🏦 Tesorería · movimientos</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Fecha</th><th>Concepto</th><th>Tipo</th><th>Monto</th></tr></thead>
            <tbody>
              {#each tesoreria as t (t.concepto)}
                <tr><td class="mono">{t.fecha}</td><td>{t.concepto}</td><td class="muted">{t.tipo}</td>
                <td class="mono {t.tipo === 'Ingreso' ? 'pos' : 'neg'}">{t.monto}</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'contabilidad'}
      <section class="card">
        <div class="card-head"><span>📊 Contabilidad · partidas del mes</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Cuenta</th><th>Debe</th><th>Haber</th></tr></thead>
            <tbody>
              {#each contabilidad as c (c.cuenta)}
                <tr><td>{c.cuenta}</td><td class="mono">{c.debe}</td><td class="mono">{c.haber}</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'nomina'}
      <section class="card">
        <div class="card-head"><span>👥 Nómina · planilla quincena</span><span class="muted">Líquido a pagar</span></div>
        <div class="card-body nopad">
          <table>
            <thead><tr><th>Empleado</th><th>Puesto</th><th>Líquido</th></tr></thead>
            <tbody>
              {#each nomina as n (n.empleado)}
                <tr><td>{n.empleado}</td><td class="muted">{n.puesto}</td><td class="mono">{n.liquido}</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>

    {:else if vista === 'reportes'}
      <section class="kpis">
        {#each reportes as r (r.ind)}
          <div class="kpi"><div class="kpi-label">{r.ind}</div><div class="kpi-value">{r.valor}</div></div>
        {/each}
      </section>
      <p class="muted" style="text-align:center;">Indicadores listos para reporte MSPAS / SIGSA.</p>
    {/if}

    <section class="cta">
      <h3>¿Te gusta lo que ves? Activá tu hospital.</h3>
      <p>30 días gratis · sin tarjeta · todo incluido (LIS, RIS, farmacia, FEL y más)</p>
      <a class="btn primary lg" href={REGISTRO}>Empezar 30 días gratis →</a>
    </section>
  </main>
</div>

<style>
  .demo {
    --aqua:#00BFC7; --aqua-dark:#0091A0; --aqua-deep:#005F73; --aqua-soft:#E6FAFB;
    --ink:#0B2E36; --muted:#5B7480; --line:#D6E7EA; --bg:#F4FBFC; --card:#fff;
    min-height:100vh; background:var(--bg); color:var(--ink);
    font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
  }
  .banner { display:flex; flex-wrap:wrap; gap:.5rem 1rem; align-items:center; justify-content:space-between;
    background:#FFF7E6; border-bottom:1px solid #F3D99B; color:#7A5A00; font-size:.86rem; padding:.5rem 1.25rem; }
  .banner a { color:#7A5A00; font-weight:600; text-decoration:underline; }
  .top { display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap;
    background:#fff; border-bottom:2px solid var(--aqua); padding:.85rem 1.25rem; }
  .brand { display:flex; align-items:center; gap:.75rem; }
  .brand img { height:42px; }
  .title .edicion { font-size:.78rem; font-weight:700; color:var(--aqua-deep); text-transform:uppercase; letter-spacing:.04em; }
  .title small { color:var(--muted); font-size:.78rem; }

  .modnav { display:flex; gap:.4rem; overflow-x:auto; padding:.6rem 1rem; background:#fff;
    border-bottom:1px solid var(--line); position:sticky; top:0; z-index:5; box-shadow:0 2px 6px rgba(0,95,115,.05); }
  .modnav button { white-space:nowrap; border:1px solid var(--line); background:#fff; color:var(--ink);
    border-radius:999px; padding:.45rem .85rem; font-size:.82rem; font-weight:600; cursor:pointer; transition:.15s; }
  .modnav button:hover { border-color:var(--aqua); color:var(--aqua-deep); }
  .modnav button.active { background:var(--aqua-deep); color:#fff; border-color:var(--aqua-deep); }

  main { max-width:1100px; margin:0 auto; padding:1.25rem 1.25rem 3rem; display:flex; flex-direction:column; gap:1.25rem; }
  .hero { display:flex; gap:1.25rem; align-items:center; background:linear-gradient(135deg,var(--aqua-deep),var(--aqua-dark));
    color:#fff; border-radius:1rem; padding:1.5rem 1.75rem; box-shadow:0 8px 24px rgba(0,95,115,.18); }
  .hero-emoji { font-size:4rem; line-height:1; }
  .hero-text h1 { margin:0; font-size:1.8rem; }
  .hero-text p { margin:.5rem 0 0; color:#DAF6F8; font-size:.95rem; max-width:46rem; }
  .hero-cta { margin-top:1rem; display:flex; flex-wrap:wrap; gap:.6rem; }
  .btn { display:inline-block; padding:.6rem 1.1rem; border-radius:.6rem; font-weight:600; text-decoration:none; font-size:.9rem; cursor:pointer; }
  .btn.primary { background:#fff; color:var(--aqua-deep); }
  .btn.primary.lg { padding:.8rem 1.6rem; font-size:1rem; }
  .btn.primary.sm { background:var(--aqua-deep); color:#fff; padding:.5rem .9rem; font-size:.84rem; }
  .pos-actions { display:flex; flex-wrap:wrap; align-items:center; gap:.5rem .8rem; margin-top:.8rem; padding-top:.7rem; border-top:1px solid var(--line); }
  .btn.ghost { border:1px solid var(--aqua); color:var(--aqua-deep); background:#fff; }
  .btn.ghost-light { border:1px solid rgba(255,255,255,.5); color:#fff; }
  .kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:.75rem; }
  .kpi { background:var(--card); border:1px solid var(--line); border-radius:.8rem; padding:.9rem 1rem; }
  .kpi-label { font-size:.72rem; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); font-weight:600; }
  .kpi-value { font-size:1.6rem; font-weight:800; color:var(--ink); margin-top:.15rem; }
  .kpi-hint { font-size:.74rem; color:var(--muted); }
  .grid2 { display:grid; grid-template-columns:1.4fr 1fr; gap:.9rem; }
  .card { background:var(--card); border:1px solid var(--line); border-radius:.9rem; overflow:hidden; }
  .card-head { display:flex; align-items:center; justify-content:space-between; gap:.5rem;
    padding:.7rem 1rem; border-bottom:1px solid var(--line); background:var(--aqua-soft); font-weight:700; color:var(--aqua-deep); font-size:.9rem; }
  .card-body { padding:1rem; }
  .card-body.nopad { padding:0; }
  .card-sub { font-size:.72rem; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); font-weight:700; margin:1rem 0 .4rem; }
  .muted { color:var(--muted); font-weight:500; font-size:.8rem; }
  .datos { display:grid; grid-template-columns:repeat(2,1fr); gap:.35rem .8rem; font-size:.86rem; }
  .datos b { color:var(--aqua-deep); font-weight:700; margin-right:.3rem; }
  .camas { display:grid; grid-template-columns:repeat(8,1fr); gap:.3rem; }
  .cama { aspect-ratio:1.4; border-radius:.35rem; display:flex; align-items:center; justify-content:center;
    font-size:.62rem; font-family:ui-monospace,monospace; color:#fff; font-weight:600; }
  .cama.ocupada { background:#E5484D; } .cama.libre { background:#30A46C; }
  .cama.limpieza { background:#F5A623; } .cama.mantenimiento { background:#8B8FA3; }
  .leyenda { display:flex; flex-wrap:wrap; gap:.5rem 1rem; margin-top:.7rem; font-size:.74rem; color:var(--muted); }
  .leyenda span { display:flex; align-items:center; gap:.3rem; }
  .dot { width:.7rem; height:.7rem; border-radius:.2rem; display:inline-block; }
  .dot.ocupada { background:#E5484D; } .dot.libre { background:#30A46C; }
  .dot.limpieza { background:#F5A623; } .dot.mantenimiento { background:#8B8FA3; }
  .signos { display:grid; grid-template-columns:repeat(3,1fr); gap:.4rem; }
  .signo { border-radius:.5rem; padding:.4rem .5rem; border:1px solid var(--line); }
  .signo.ok { background:#E9F8F0; border-color:#BFE9D2; }
  .signo.warn { background:#FFF4E0; border-color:#F3D99B; }
  .s-label { font-size:.66rem; color:var(--muted); }
  .s-value { font-size:1.05rem; font-weight:700; }
  .dx { font-size:.85rem; color:var(--ink); margin:.8rem 0 0; }
  .evo { list-style:none; margin:.2rem 0 0; padding:0; display:flex; flex-direction:column; gap:.55rem; }
  .evo li { display:flex; gap:.7rem; align-items:flex-start; }
  .evo li span:last-child { display:flex; flex-direction:column; }
  .evo small { color:var(--muted); font-size:.8rem; }
  table { width:100%; border-collapse:collapse; font-size:.84rem; }
  th { text-align:left; font-size:.7rem; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); padding:.5rem .8rem; background:#F7FCFD; }
  td { padding:.55rem .8rem; border-top:1px solid var(--line); }
  .mono { font-family:ui-monospace,monospace; }
  .mono.pos { color:#1B8A5A; } .mono.neg { color:#C13515; }
  .badge { font-size:.72rem; background:var(--aqua-soft); color:var(--aqua-deep); border:1px solid var(--aqua); border-radius:999px; padding:.1rem .55rem; }
  .badge.ok { background:#E9F8F0; color:#1B8A5A; border-color:#BFE9D2; }
  .badge.warn { background:#FFF4E0; color:#9A6700; border-color:#F3D99B; }
  .ticket { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.45rem; }
  .ticket li { display:flex; justify-content:space-between; gap:1rem; font-size:.86rem; }
  .ticket-total { display:flex; justify-content:space-between; margin-top:.7rem; padding-top:.6rem; border-top:2px solid var(--line); font-weight:800; }
  .cta { text-align:center; background:var(--ink); color:#fff; border-radius:1rem; padding:2rem 1.5rem; }
  .cta h3 { margin:0; font-size:1.5rem; }
  .cta p { margin:.5rem 0 1.2rem; color:#B9D6DC; font-size:.9rem; }
  @media (max-width:820px) {
    .kpis { grid-template-columns:repeat(2,1fr); }
    .grid2 { grid-template-columns:1fr; }
    .hero { flex-direction:column; align-items:flex-start; }
  }
</style>
